import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Settings } from '../../models/settings.model';
import { legendValid } from '../../utils/legend.data.util';
import { FeatureDetail } from '../../models/feature.detail.model';
import { prefixLegend, suffixLegend } from '../../scripts/name.legend.script';

import AppLegend from './Legend';

export interface AppInputFeatureOptionsProps {
  component?: string,
  options: {index: number},
  settingsForCompany: Settings,
  features?: {value: FeatureDetail[], valid: boolean[]},
  variant?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  setFeatures?: Dispatch<SetStateAction<{value: FeatureDetail[], valid: boolean[]}>>,
  setVariant?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>
};

const AppInputFeatureOptions: React.FunctionComponent<AppInputFeatureOptionsProps> = ({component, settingsForCompany, options, features, variant, setFeatures, setVariant}) => {
  const {lang} = StateLanguage()

  const handleChangeFeatureOptions = (item: string) => {
    if (features && setFeatures) {
      let tempFeaturesValue = features.value.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item.value = item
        }
        return temp_item
      })
      let tempFeaturesValid = features.valid.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item = true
        }
        return temp_item
      })

      setFeatures({value: tempFeaturesValue, valid: tempFeaturesValid})
      legendValid(`${prefixLegend(component)}legend-feature-required${suffixLegend(options.index, options.index)}`)
    }
  }

  const handleChangeFeatureOptionsToVariant = (item: string) => {
    if (variant && setVariant) {
      let tempVariantFeaturesValue = variant.value.features.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item.value = item
        }
        return temp_item
      })
      let tempVariantFeaturesValid = variant.valid.features.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item = true
        }
        return temp_item
      })
      setVariant({value: {...variant.value, features: tempVariantFeaturesValue}, valid: {...variant.valid, features: tempVariantFeaturesValid}})
      legendValid(`${prefixLegend(component)}legend-variant-feature-required${suffixLegend(options.index)}`)
    }
  }

  function featureOptions(): string[] {
    if (settingsForCompany && features && setFeatures) {
      return settingsForCompany.features.find((item) => (item.id === features.value[options.index].id_feature))?.options ?? []
    } else if (settingsForCompany && variant && setVariant) {
      return settingsForCompany.features.find((item) => (item.id === variant.value.features[options.index].id_feature))?.options ?? []
    } else {
      return []
    }
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <span className="select2-selection select2-selection--single form-select bg-light">
              <span className="select2-selection__rendered" role="textbox">
                { features && setFeatures &&
                  <>
                    {features.value[options.index].value.length > 0 ? features.value[options.index].value : lang.labels.selectOption}
                  </>
                }
                { variant && setVariant &&
                  <>
                    {variant.value.features[options.index].value.length > 0 ? variant.value.features[options.index].value : lang.labels.selectOption}
                  </>
                }
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox">
                { features && setFeatures && featureOptions().map (( (item, index) => { return (
                  <li key={index} className={`select2-results__option select2-results__option--selectable ${item === features.value[options.index].value && "select2-results__option--selected"}`} role="option" aria-selected={item === features.value[options.index].value}>
                    <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeFeatureOptions(item)}>{item}</Dropdown.Item>
                  </li>
                )}))}
                { variant && setVariant && featureOptions().map (( (item, index) => { return (
                  <li key={index} className={`select2-results__option select2-results__option--selectable ${item === variant.value.features[options.index].value && "select2-results__option--selected"}`} role="option" aria-selected={item === variant.value.features[options.index].value}>
                    <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeFeatureOptionsToVariant(item)}>{item}</Dropdown.Item>
                  </li>
                )}))}
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { features && setFeatures &&
        <AppLegend attribute={{check: features.valid[options.index], name: "feature", index: options.index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
      }
      { variant && setVariant && 
        <>
          <AppLegend component={component} attribute={{check: variant.valid.features[options.index], name: "variant-feature", index: options.index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
        </>
      }
    </>
  )
}

export default AppInputFeatureOptions;
